import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader, X, ArrowRight, ArrowLeft, Scissors, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { renderValue } from '../lib/knowledge';
import SourceLink from './SourceLink';
import type { KnowledgeRecordDetail } from '../lib/types';
import OrganizationRole from './OrganizationRole';

// Drill-down: a record's properties, what it was consolidated from, and its
// relationships. Clicking a related record traverses to it, so you can walk the
// graph.
export default function RecordDetailModal({ target, onNavigate, onClose, onChanged }: {
    target: { label: string; key: string };
    onNavigate: (label: string, key: string) => void;
    onClose: () => void;
    onChanged: () => void;
}) {
    const { authFetch } = useAuth();
    const [record, setRecord] = useState<KnowledgeRecordDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let stale = false;
        setLoading(true);
        authFetch(`/knowledge/records/${encodeURIComponent(target.label)}/${encodeURIComponent(target.key)}`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load')))
            .then((d: KnowledgeRecordDetail) => { if (!stale) setRecord(d); })
            .catch(() => { if (!stale) setRecord(null); })
            .finally(() => { if (!stale) setLoading(false); });
        return () => { stale = true; };
    }, [target.label, target.key, reload]);

    const readAs = async (memberKey: string) => {
        setBusy(true);
        try {
            const res = await authFetch('/knowledge/records/lead', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: target.label, key: target.key, lead: memberKey,
                }),
            });
            if (res.ok) { setReload(n => n + 1); onChanged(); }
        } finally {
            setBusy(false);
        }
    };

    const split = async (memberKey: string) => {
        setBusy(true);
        try {
            const res = await authFetch('/knowledge/records/split', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ label: target.label, key: memberKey }),
            });
            if (res.ok) { setReload(n => n + 1); onChanged(); }
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const heading = record?.name ?? target.key;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-graphite/30" onClick={onClose}>
            <div
                className="bg-white rounded-xl border border-softgrey shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b border-softgrey flex items-start justify-between gap-3 sticky top-0 bg-white">
                    <div className="min-w-0">
                        <h4 className="navaris-h3 truncate">{heading}</h4>
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <code className="text-[11px] text-coolslate">{target.label}</code>
                            <OrganizationRole role={record?.ours} />
                            {record?.pending && (
                                <Link
                                    to={record.review_id ? `/review?id=${record.review_id}` : '/review'}
                                    className="px-1.5 py-0.5 rounded-md border border-dashed border-state-warning bg-state-warning/15 text-[11px] font-medium text-graphite"
                                >
                                    {record.pending === 'new' ? 'Pending review, not on the record yet' : 'A change is pending review'}
                                </Link>
                            )}
                            {record?.is_consolidated && (
                                <span className="text-[11px] text-brand">
                                    consolidated from {record.members.length} records
                                </span>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 -m-1 text-coolslate hover:text-graphite shrink-0" aria-label="Close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12"><Loader className="w-6 h-6 animate-spin text-brand" /></div>
                ) : (
                    <div className="p-5 space-y-5">
                        {record && Object.keys(record.properties).length > 0 && (
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-coolslate/70 mb-2">Properties</p>
                                <dl className="space-y-1">
                                    {Object.entries(record.properties).map(([k, v]) => (
                                        <div key={k} className="flex gap-3 text-[13px]">
                                            <dt className="w-40 shrink-0 text-coolslate">{k}</dt>
                                            <dd className="text-graphite min-w-0 break-words">
                                                {renderValue(v)}
                                                {record.contributions[k]?.source && (
                                                    <span className="text-[11px] text-coolslate/70">
                                                        {' '}· {record.contributions[k].source?.name}
                                                    </span>
                                                )}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}

                        {record && record.members.length > 0 && (
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-coolslate/70 mb-2">
                                    {record.is_consolidated ? 'Consolidated from' : 'Source'}
                                </p>
                                <div className="space-y-2">
                                    {record.members.map(member => (
                                        <div key={member.resource_key} className="flex items-start justify-between gap-3 text-[13px]">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {member.observations.map((n, i) => (
                                                        <span key={i} className="text-graphite">
                                                            {n.source ? <SourceLink source={n.source} /> : 'Worked out'}
                                                            {n.confidence != null && n.confidence < 1
                                                                ? <span className="text-coolslate/70"> · {Math.round(n.confidence * 100)}% confidence</span>
                                                                : null}
                                                        </span>
                                                    ))}
                                                </div>
                                                <code className="block text-[11px] text-coolslate/60 break-all">{member.resource_key}</code>
                                                {record.lead === member.resource_key && (
                                                    <span className="text-[11px] text-brand">
                                                        the record reads as this one
                                                    </span>
                                                )}
                                                {record.is_consolidated && member.can_lead === false && (
                                                    <span className="text-[11px] text-coolslate">
                                                        a claim read off a document cannot lead the record
                                                    </span>
                                                )}
                                                {record.is_consolidated && (
                                                    <span className="text-[11px] text-coolslate">
                                                        {member.decided_by === 'person'
                                                            ? 'joined because someone said so'
                                                            : member.rule
                                                                ? `matched on ${member.rule.replace(/_/g, ' ')}${member.match_value ? `: ${member.match_value}` : ''}`
                                                                : ''}
                                                    </span>
                                                )}
                                            </div>
                                            {record.is_consolidated && (
                                                <div className="shrink-0 flex items-center gap-2">
                                                    {record.lead !== member.resource_key && member.can_lead !== false && (
                                                        <button
                                                            onClick={() => readAs(member.resource_key)}
                                                            disabled={busy}
                                                            title="Show the record as this one describes it"
                                                            className="inline-flex items-center gap-1 px-2 py-1 text-[12px] rounded-lg border border-softgrey text-graphite hover:bg-offwhite disabled:opacity-50"
                                                        >
                                                            <Star className="w-3.5 h-3.5" /> Read as this
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => split(member.resource_key)}
                                                        disabled={busy}
                                                        title="Separate this from the record"
                                                        className="inline-flex items-center gap-1 px-2 py-1 text-[12px] rounded-lg border border-softgrey text-graphite hover:bg-offwhite disabled:opacity-50"
                                                    >
                                                        <Scissors className="w-3.5 h-3.5" /> Separate
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-coolslate/70 mb-2">
                                Relationships{record && record.relationships.length > 0 ? ` (${record.relationships.length})` : ''}
                            </p>
                            {record && record.relationships.length > 0 ? (
                                <div className="space-y-1.5">
                                    {record.relationships.map((e, i) => (
                                        <div key={i} className="flex items-start gap-2 text-[13px]">
                                            {e.direction === 'out'
                                                ? <ArrowRight className="w-3.5 h-3.5 text-coolslate/60 shrink-0 mt-0.5" />
                                                : <ArrowLeft className="w-3.5 h-3.5 text-coolslate/60 shrink-0 mt-0.5" />}
                                            <div className="min-w-0">
                                                <span className="text-coolslate">{e.type}</span>{' '}
                                                <button
                                                    onClick={() => onNavigate(e.node.label, e.node.key)}
                                                    className="text-brand hover:text-brand-dark hover:underline font-medium break-words"
                                                >
                                                    {e.node.name}
                                                </button>{' '}
                                                <span className="text-[11px] text-coolslate/70">({e.node.label})</span>
                                                {e.pending && (
                                                    <Link
                                                        to={e.review_id ? `/review?id=${e.review_id}` : '/review'}
                                                        className="ml-1.5 px-1 py-0.5 rounded border border-dashed border-state-warning bg-state-warning/15 text-[10px] font-medium text-graphite"
                                                    >
                                                        Pending review
                                                    </Link>
                                                )}
                                                {Object.keys(e.properties).length > 0 && (
                                                    <span className="block text-[11px] text-coolslate/80">
                                                        {Object.entries(e.properties).map(([k, v]) => `${k}: ${renderValue(v)}`).join('  ·  ')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-[13px] text-coolslate">No relationships recorded.</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
